import { useEffect, useState } from 'react';
import {
  Card,
  Button,
  Space,
  Typography,
  Tag,
  Switch,
  Input,
  Select,
  Form,
  Dropdown,
  Empty,
  message,
} from 'antd';
import {
  SaveOutlined,
  PlusOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { updateApp, type App } from '@/api/apps';
import { listCategories, type PackageCategory } from '@/api/content';

const { Paragraph, Text } = Typography;

/**
 * HomeLayoutEditor — 结构化编排 apps.routes.home.sections (configurable-home change).
 *
 * 固定 section 类型 (banner / entry_grid / package_category / recommend) 的有序列表，
 * 运营可增/删/上下排序/显隐 + 按 type 编辑内容。保存时把编排结果合并进 routes.home，
 * 保留 routes 其它键 (nav_tabs / profile 等)，调现有 PATCH /admin/v1/apps/:id 写回。
 *
 * 不做拖拽建站 — 结构化表单 + 上下移 (对齐 design.md D5 / Risks)。
 */

// ---- section schema (契约, 严格对齐 spec/design) ---------------------------

export type SectionType = 'banner' | 'entry_grid' | 'package_category' | 'recommend';

interface BannerItem {
  image_key?: string;
  link?: string;
  title?: string;
}
interface EntryItem {
  key?: string;
  label?: string;
  icon?: string;
  target?: string;
}

export interface HomeSection {
  type: SectionType;
  visible?: boolean;
  // banner 图片列表
  items?: BannerItem[];
  // entry_grid 入口项 (后端契约字段名 entry_items, 与 banner.items 区分)
  entry_items?: EntryItem[];
  // 通用标题 (entry_grid / package_category / recommend)
  title?: string;
  // package_category
  category?: string;
  layout?: string;
  // recommend
  source?: string;
}

const SECTION_LABEL: Record<SectionType, string> = {
  banner: 'Banner 轮播',
  entry_grid: '入口宫格',
  package_category: '套系分类',
  recommend: '推荐',
};

const SECTION_DESC: Record<SectionType, string> = {
  banner: '顶部轮播图，每项 image_key + link + title',
  entry_grid: '入口宫格 (创意工具等)，每项 label + icon + target',
  package_category: '按 category 拉取套系列表横滑/网格展示',
  recommend: '按 source (hot/new/...) 拉取推荐套系',
};

function newSection(type: SectionType): HomeSection {
  switch (type) {
    case 'banner':
      return { type, visible: true, items: [] };
    case 'entry_grid':
      return { type, visible: true, title: '创作工具', entry_items: [] };
    case 'package_category':
      return { type, visible: true, title: '', category: '', layout: 'h-scroll' };
    case 'recommend':
      return { type, visible: true, title: '热门', source: 'hot' };
  }
}

// routes.home.sections 安全读取
function readSections(routes: Record<string, unknown> | null | undefined): HomeSection[] {
  const home = (routes?.home ?? null) as { sections?: unknown } | null;
  const raw = Array.isArray(home?.sections) ? (home!.sections as unknown[]) : [];
  return raw
    .filter((s): s is HomeSection => !!s && typeof s === 'object' && 'type' in (s as object))
    .map((s) => ({ visible: true, ...(s as HomeSection) }));
}

export function HomeLayoutEditor({ app, onSaved }: { app: App; onSaved?: (a: App) => void }) {
  const [sections, setSections] = useState<HomeSection[]>(() => readSections(app.routes));
  const [categories, setCategories] = useState<PackageCategory[]>([]);
  const [catLoadFailed, setCatLoadFailed] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setSections(readSections(app.routes));
  }, [app.routes]);

  useEffect(() => {
    listCategories(app.id)
      .then((c) => setCategories(Array.isArray(c) ? c : []))
      .catch(() => setCatLoadFailed(true));
  }, [app.id]);

  const update = (i: number, patch: Partial<HomeSection>) => {
    setSections((prev) => prev.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));
  };
  const move = (i: number, dir: -1 | 1) => {
    setSections((prev) => {
      const j = i + dir;
      if (j < 0 || j >= prev.length) return prev;
      const next = [...prev];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  };
  const remove = (i: number) => setSections((prev) => prev.filter((_, idx) => idx !== i));
  const add = (type: SectionType) => setSections((prev) => [...prev, newSection(type)]);

  const onSave = async () => {
    setSaving(true);
    try {
      // 合并: 保留 routes 其它键 (nav_tabs / profile / ...)，只覆盖 home.sections。
      const prevRoutes = (app.routes ?? {}) as Record<string, unknown>;
      const prevHome = (prevRoutes.home ?? {}) as Record<string, unknown>;
      const routes: Record<string, unknown> = {
        ...prevRoutes,
        home: { ...prevHome, sections },
      };
      const updated = await updateApp(app.id, { routes });
      message.success('首页编排已保存');
      onSaved?.(updated);
    } catch (e) {
      message.error(e instanceof Error ? e.message : '保存失败');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card
      extra={
        <Space>
          <Dropdown
            menu={{
              items: (Object.keys(SECTION_LABEL) as SectionType[]).map((t) => ({
                key: t,
                label: SECTION_LABEL[t],
                onClick: () => add(t),
              })),
            }}
          >
            <Button icon={<PlusOutlined />}>添加区块</Button>
          </Dropdown>
          <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={onSave}>
            保存编排
          </Button>
        </Space>
      }
    >
      <Paragraph type="secondary">
        结构化编排首页区块 (routes.home.sections)。增/删/上下移调整顺序，显隐控制是否渲染。
        保存只覆盖 home.sections，保留 routes 其它键 (nav_tabs / profile)。空 = 客户端回退现有套系列表。
      </Paragraph>

      {sections.length === 0 ? (
        <Empty description="尚无区块，点击右上「添加区块」开始编排" />
      ) : (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          {sections.map((s, i) => (
            <SectionCard
              key={`${s.type}-${i}`}
              index={i}
              total={sections.length}
              section={s}
              categories={categories}
              catLoadFailed={catLoadFailed}
              onChange={(p) => update(i, p)}
              onMove={(d) => move(i, d)}
              onRemove={() => remove(i)}
            />
          ))}
        </Space>
      )}
    </Card>
  );
}

// ---- per-section card ------------------------------------------------------

function SectionCard({
  index,
  total,
  section,
  categories,
  catLoadFailed,
  onChange,
  onMove,
  onRemove,
}: {
  index: number;
  total: number;
  section: HomeSection;
  categories: PackageCategory[];
  catLoadFailed: boolean;
  onChange: (patch: Partial<HomeSection>) => void;
  onMove: (dir: -1 | 1) => void;
  onRemove: () => void;
}) {
  return (
    <Card
      size="small"
      type="inner"
      title={
        <Space>
          <Tag color="blue">{index + 1}</Tag>
          <Text strong>{SECTION_LABEL[section.type]}</Text>
          <Tag>{section.type}</Tag>
        </Space>
      }
      extra={
        <Space>
          <Switch
            checkedChildren="显示"
            unCheckedChildren="隐藏"
            checked={section.visible !== false}
            onChange={(v) => onChange({ visible: v })}
          />
          <Button
            size="small"
            icon={<ArrowUpOutlined />}
            disabled={index === 0}
            onClick={() => onMove(-1)}
          />
          <Button
            size="small"
            icon={<ArrowDownOutlined />}
            disabled={index === total - 1}
            onClick={() => onMove(1)}
          />
          <Button size="small" danger icon={<DeleteOutlined />} onClick={onRemove} />
        </Space>
      }
    >
      <Paragraph type="secondary" style={{ marginTop: 0 }}>
        {SECTION_DESC[section.type]}
      </Paragraph>
      <SectionBody
        section={section}
        categories={categories}
        catLoadFailed={catLoadFailed}
        onChange={onChange}
      />
    </Card>
  );
}

function SectionBody({
  section,
  categories,
  catLoadFailed,
  onChange,
}: {
  section: HomeSection;
  categories: PackageCategory[];
  catLoadFailed: boolean;
  onChange: (patch: Partial<HomeSection>) => void;
}) {
  switch (section.type) {
    case 'banner':
      return <BannerBody section={section} onChange={onChange} />;
    case 'entry_grid':
      return <EntryGridBody section={section} onChange={onChange} />;
    case 'package_category':
      return (
        <PackageCategoryBody
          section={section}
          categories={categories}
          catLoadFailed={catLoadFailed}
          onChange={onChange}
        />
      );
    case 'recommend':
      return <RecommendBody section={section} onChange={onChange} />;
  }
}

// ---- banner ---------------------------------------------------------------

function BannerBody({
  section,
  onChange,
}: {
  section: HomeSection;
  onChange: (patch: Partial<HomeSection>) => void;
}) {
  const items = (section.items ?? []) as BannerItem[];
  const setItems = (next: BannerItem[]) => onChange({ items: next });
  const setItem = (i: number, p: Partial<BannerItem>) =>
    setItems(items.map((it, idx) => (idx === i ? { ...it, ...p } : it)));
  return (
    <Space direction="vertical" size={8} style={{ width: '100%' }}>
      {items.map((it, i) => (
        <Space key={i} align="start" wrap>
          <Input
            placeholder="image_key (OSS key 或外链)"
            style={{ width: 240 }}
            value={it.image_key}
            onChange={(e) => setItem(i, { image_key: e.target.value })}
          />
          <Input
            placeholder="link (target deeplink 或 url)"
            style={{ width: 220 }}
            value={it.link}
            onChange={(e) => setItem(i, { link: e.target.value })}
          />
          <Input
            placeholder="title"
            style={{ width: 160 }}
            value={it.title}
            onChange={(e) => setItem(i, { title: e.target.value })}
          />
          <Button danger icon={<DeleteOutlined />} onClick={() => setItems(items.filter((_, idx) => idx !== i))} />
        </Space>
      ))}
      <Button
        type="dashed"
        icon={<PlusOutlined />}
        onClick={() => setItems([...items, { image_key: '', link: '', title: '' }])}
      >
        添加图
      </Button>
    </Space>
  );
}

// ---- entry_grid -----------------------------------------------------------

function EntryGridBody({
  section,
  onChange,
}: {
  section: HomeSection;
  onChange: (patch: Partial<HomeSection>) => void;
}) {
  const items = (section.entry_items ?? []) as EntryItem[];
  const setItems = (next: EntryItem[]) => onChange({ entry_items: next });
  const setItem = (i: number, p: Partial<EntryItem>) =>
    setItems(items.map((it, idx) => (idx === i ? { ...it, ...p } : it)));
  return (
    <Form layout="vertical" size="small">
      <Form.Item label="标题">
        <Input
          style={{ maxWidth: 320 }}
          placeholder="创作工具"
          value={section.title}
          onChange={(e) => onChange({ title: e.target.value })}
        />
      </Form.Item>
      <Space direction="vertical" size={8} style={{ width: '100%' }}>
        {items.map((it, i) => (
          <Space key={i} align="start" wrap>
            <Input
              placeholder="key"
              style={{ width: 130 }}
              value={it.key}
              onChange={(e) => setItem(i, { key: e.target.value })}
            />
            <Input
              placeholder="label"
              style={{ width: 130 }}
              value={it.label}
              onChange={(e) => setItem(i, { label: e.target.value })}
            />
            <Input
              placeholder="icon (key/url)"
              style={{ width: 150 }}
              value={it.icon}
              onChange={(e) => setItem(i, { icon: e.target.value })}
            />
            <Input
              placeholder="target: tool:image_edit"
              style={{ width: 220 }}
              value={it.target}
              onChange={(e) => setItem(i, { target: e.target.value })}
            />
            <Button danger icon={<DeleteOutlined />} onClick={() => setItems(items.filter((_, idx) => idx !== i))} />
          </Space>
        ))}
        <Button
          type="dashed"
          icon={<PlusOutlined />}
          onClick={() => setItems([...items, { key: '', label: '', icon: '', target: '' }])}
        >
          添加入口
        </Button>
        <Text type="secondary">
          target 语义：tool:&lt;id&gt; / package:&lt;id&gt; / category:&lt;slug&gt; / url:&lt;...&gt;
        </Text>
      </Space>
    </Form>
  );
}

// ---- package_category -----------------------------------------------------

function PackageCategoryBody({
  section,
  categories,
  catLoadFailed,
  onChange,
}: {
  section: HomeSection;
  categories: PackageCategory[];
  catLoadFailed: boolean;
  onChange: (patch: Partial<HomeSection>) => void;
}) {
  return (
    <Form layout="vertical" size="small">
      <Form.Item label="标题">
        <Input
          style={{ maxWidth: 320 }}
          placeholder="港风"
          value={section.title}
          onChange={(e) => onChange({ title: e.target.value })}
        />
      </Form.Item>
      <Form.Item
        label="分类 (category slug)"
        help={catLoadFailed ? '分类接口加载失败，请手动输入 slug' : undefined}
        validateStatus={catLoadFailed ? 'warning' : undefined}
      >
        {catLoadFailed || categories.length === 0 ? (
          <Input
            style={{ maxWidth: 320 }}
            placeholder="hk_vintage"
            value={section.category}
            onChange={(e) => onChange({ category: e.target.value })}
          />
        ) : (
          <Select
            style={{ maxWidth: 320, width: '100%' }}
            placeholder="选择套系分类"
            showSearch
            allowClear
            value={section.category || undefined}
            onChange={(v) => onChange({ category: v ?? '' })}
            optionFilterProp="label"
            options={categories.map((c) => ({ value: c.slug, label: `${c.name} (${c.slug})` }))}
          />
        )}
      </Form.Item>
      <Form.Item label="布局">
        <Select
          style={{ maxWidth: 320, width: '100%' }}
          value={section.layout ?? 'h-scroll'}
          onChange={(v) => onChange({ layout: v })}
          options={[
            { value: 'h-scroll', label: '横向滚动 (h-scroll)' },
            { value: 'grid', label: '网格 (grid)' },
          ]}
        />
      </Form.Item>
    </Form>
  );
}

// ---- recommend ------------------------------------------------------------

function RecommendBody({
  section,
  onChange,
}: {
  section: HomeSection;
  onChange: (patch: Partial<HomeSection>) => void;
}) {
  return (
    <Form layout="vertical" size="small">
      <Form.Item label="标题">
        <Input
          style={{ maxWidth: 320 }}
          placeholder="热门"
          value={section.title}
          onChange={(e) => onChange({ title: e.target.value })}
        />
      </Form.Item>
      <Form.Item label="数据源 (source)">
        <Select
          style={{ maxWidth: 320, width: '100%' }}
          value={section.source ?? 'hot'}
          onChange={(v) => onChange({ source: v })}
          options={[
            { value: 'hot', label: '热门 (hot)' },
            { value: 'new', label: '最新 (new)' },
          ]}
        />
      </Form.Item>
    </Form>
  );
}
